import type { stats } from "@app/domain";
import type { Member } from "@app/protocol";
import { useEffect, useId, useRef } from "react";
import { PlayerCardBody } from "./PlayerCard";

/**
 * A player card over the leaderboard (review 0017 S2, S4, S5): a modal dialog, so focus stays in
 * it, Escape or a tap outside closes it and focus returns to the row that opened it.
 */
export function CardDialog({
  member,
  rank,
  onClose,
}: {
  member: Pick<Member, "userId" | "name" | "avatar">;
  rank: stats.RankFrame | null;
  onClose: () => void;
}) {
  const labelId = useId();
  const dialog = useRef<HTMLDialogElement>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    const node = dialog.current;
    const back = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    // A tap on the backdrop lands on the dialog element itself; Escape is the keyboard's way out.
    const onBackdrop = (event: MouseEvent) => {
      if (event.target === node) onClose();
    };
    node?.addEventListener("click", onBackdrop);
    node?.showModal();
    heading.current?.focus();
    return () => {
      node?.removeEventListener("click", onBackdrop);
      node?.close();
      back?.focus({ preventScroll: true });
    };
  }, [onClose]);
  return (
    <dialog
      ref={dialog}
      aria-labelledby={labelId}
      className="m-auto w-[min(340px,calc(100vw-32px))] rounded-[20px] border-[1.5px] border-edge bg-panel-solid p-4 text-ink shadow-panel backdrop:bg-ground/65 motion-safe:animate-rise"
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
    >
      <PlayerCardBody
        member={member}
        rank={rank}
        labelId={labelId}
        headingRef={heading}
        onClose={onClose}
      />
    </dialog>
  );
}
